"use client"

import React, { useState } from "react"
import "@/styles/input.css"

interface InputProps {
  label: string
  value: string | number
  onChange: (value: string) => void
  type?: "text" | "number" | "email" | "password" | "url" | "date" | "time" | "datetime-local" | "tel"
  // Codex note: Panel form inputs do not use placeholders. Use `label` and helper text instead.
  // Kept for legacy call sites, but intentionally not rendered on the native input.
  placeholder?: string
  required?: boolean
  disabled?: boolean
  error?: string
  helperText?: string
  rightIcon?: string
  rightButton?: {
    text: string
    onClick: () => void
  }

  /**
   * Optional passthrough props for the underlying <input />.
   * Useful for handling key events (Enter), autoFocus, etc.
   */
  inputProps?: Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type" | "placeholder" | "disabled">

  /** Optional ref for focusing the input from parent components. */
  inputRef?: React.Ref<HTMLInputElement>
}

export function Input({
  label,
  value,
  onChange,
  type = "text",
  placeholder: _placeholder,
  required = false,
  disabled = false,
  error,
  helperText,
  rightIcon,
  rightButton,
  inputProps,
  inputRef
}: InputProps) {
  const [isFocused, setIsFocused] = useState(false)
  const hasValue = String(value).length > 0 && value !== null && value !== undefined
  const hasError = !!error
  const isActive = isFocused || hasValue
  const hasRight = !!(rightIcon || rightButton)

  return (
    <div className="fi-wrapper">
      <div className="fi-inner">
        <input
          ref={inputRef}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
          className={`fi-input${hasRight ? " fi-input--has-right" : ""}`}
          data-active={isActive || undefined}
          data-error={hasError || undefined}
          {...(inputProps || {})}
        />
        {label && (
          <label
            className={`fi-label${isActive ? " fi-label--float" : ""}`}
            data-error={hasError || undefined}
            data-disabled={disabled || undefined}
          >
            {label} {required && <span className="fi-required">*</span>}
          </label>
        )}
        {rightIcon && (
          <div className="fi-right-icon">
            <i className={rightIcon} />
          </div>
        )}
        {rightButton && (
          <button
            type="button"
            onClick={rightButton.onClick}
            className={`fi-right-btn${rightIcon ? " fi-right-btn--with-icon" : " fi-right-btn--no-icon"}`}
          >
            {rightButton.text}
          </button>
        )}
      </div>
      {(error || helperText) && (
        <div className={`fi-helper${error ? " fi-helper--error" : ""}`}>
          {error || helperText}
        </div>
      )}
    </div>
  )
}

interface TextareaProps {
  label: string
  value: string
  onChange: (value: string) => void
  // Codex note: Panel form textareas do not use placeholders. Use `label` and helper text instead.
  // Kept for legacy call sites, but intentionally not rendered on the native textarea.
  placeholder?: string
  required?: boolean
  disabled?: boolean
  error?: string
  helperText?: string
  rows?: number
  textareaProps?: Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "onChange" | "placeholder" | "disabled" | "rows">
}

export function Textarea({
  label,
  value,
  onChange,
  placeholder: _placeholder,
  required = false,
  disabled = false,
  error,
  helperText,
  rows = 3,
  textareaProps
}: TextareaProps) {
  const [isFocused, setIsFocused] = useState(false)
  const hasValue = String(value).length > 0 && value !== null && value !== undefined
  const hasError = !!error
  const isActive = isFocused || hasValue

  return (
    <div className="fi-wrapper">
      <div className="fi-inner">
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
          rows={rows}
          className="fi-textarea"
          data-active={isActive || undefined}
          data-error={hasError || undefined}
          {...(textareaProps || {})}
        />
        {label && (
          <label
            className={`fi-label${isActive ? " fi-label--float" : ""}`}
            data-error={hasError || undefined}
            data-disabled={disabled || undefined}
          >
            {label} {required && <span className="fi-required">*</span>}
          </label>
        )}
      </div>
      {(error || helperText) && (
        <div className={`fi-helper${error ? " fi-helper--error" : ""}`}>
          {error || helperText}
        </div>
      )}
    </div>
  )
}

interface SelectProps {
  label: string
  value: string | number
  onChange: (value: string) => void
  options: { value: string | number; label: string; disabled?: boolean; title?: string }[]
  required?: boolean
  disabled?: boolean
  error?: string
  helperText?: string
  placeholder?: string
  alwaysShowLabel?: boolean
  onFocus?: () => void
  onMouseDown?: React.MouseEventHandler<HTMLSelectElement>
}

export function Select({
  label,
  value,
  onChange,
  options,
  required = false,
  disabled = false,
  error,
  helperText,
  placeholder = 'Seçiniz',
  alwaysShowLabel = false,
  onFocus,
  onMouseDown
}: SelectProps) {
  const [isFocused, setIsFocused] = useState(false)
  const hasValue = value !== "" && value !== null && value !== undefined
  const hasMatchingValue = hasValue && options.some((option) => String(option.value) === String(value))
  const hasError = !!error

  return (
    <div className="fi-wrapper">
      <div className="fi-inner">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => { setIsFocused(true); onFocus?.() }}
          onMouseDown={onMouseDown}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
          className="fi-select"
          data-active={(isFocused || hasValue || alwaysShowLabel) || undefined}
          data-error={hasError || undefined}
        >
          <option value="">{placeholder}</option>
          {hasValue && !hasMatchingValue && (
            <option value={String(value)}>{String(value)}</option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled} title={option.title}>
              {option.label}
            </option>
          ))}
        </select>
        {(hasValue || alwaysShowLabel) && (
          <label
            className="fi-select-label"
            data-focused={isFocused || undefined}
            data-error={hasError || undefined}
            data-disabled={disabled || undefined}
          >
            {label} {required && <span className="fi-required">*</span>}
          </label>
        )}
      </div>
      {(error || helperText) && (
        <div className={`fi-select-helper${error ? " fi-select-helper--error" : ""}`}>
          {error || helperText}
        </div>
      )}
    </div>
  )
}

interface FormGroupProps {
  children: React.ReactNode
  columns?: number
}

export function FormGroup({ children, columns = 1 }: FormGroupProps) {
  return (
    <div
      className="fi-form-group"
      style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
    >
      {children}
    </div>
  )
}
